import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  Blocks,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Eye,
  Grid2X2,
  Network,
  Search,
  ShieldCheck,
  TicketCheck,
  UserRoundSearch,
  Wallet,
} from 'lucide-react';
import { Fragment, type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isAddress } from 'viem';
import { MatrixGraph } from '../components/MatrixGraph';
import { GenerationExplorer } from '../components/GenerationExplorer';
import { GenerationIncomeMap } from '../components/GenerationIncomeMap';
import { ClubCard } from '../components/ClubCard';
import { IdReportsPanel, SlotStateTable } from '../components/IdReports';
import { PrimaryTokenCard } from '../components/PrimaryTokenCard';
import { CdtBenefitCard } from '../components/CdtBenefitCard';
import { OnChainMatrixSection } from '../components/OnChainMatrixCard';
import { CycleTabs, ProjectedSlotPanel, SlotTrack, TrackLegend } from '../components/SlotMatrix';
import { POSITION_KIND_LABEL, positionKindTone } from '../components/PositionGrid';
import { EmptyState, PageHeading, StatusPill } from '../components/Ui';
import type { MatrixNode } from '../data/view-model';
import { ApiError, api } from '../lib/api';
import { explorerUrlFor } from '../lib/network';
import { formatDate, formatToken, isMemberId, shortenAddress } from '../lib/format';
import type { CycleDetail, LookupResult } from '../lib/lookup-types';
import { slotLabel } from '../lib/slots';
import { atomicToDecimal } from '../state/AppDataContext';
import { useWallet } from '../state/WalletContext';

/** Single-leg node array of one on-chain cycle (indexed SeatFilled seats), coloured by the seat kind. */
function buildMatrixNodes(cycle: CycleDetail): MatrixNode[] {
  const positions = Math.max(12, cycle.totalPositions);
  return Array.from({ length: positions }, (_, index) => {
    const position = index + 1;
    const placement = cycle.placements.find((item) => item.positionNumber === position);
    if (!placement) return { position, kind: 'empty', state: 'empty' };
    return {
      position,
      memberId: placement.memberBdId,
      kind: placement.kind ? positionKindTone(placement.kind) : placement.placementType === 'virtual' ? 'treasury' : 'downline',
      state: 'confirmed',
    };
  });
}

type LookupKind = 'member' | 'wallet' | 'transaction' | 'unknown';
type SearchState = 'idle' | 'loading' | 'found' | 'invalid' | 'not-found' | 'error';

const explorerUrl = (chainId: number): string => explorerUrlFor(chainId);

function detectKind(query: string): LookupKind {
  const value = query.trim();
  if (isMemberId(value)) return 'member';
  if (/^0x[a-fA-F0-9]{64}$/.test(value)) return 'transaction';
  if (isAddress(value)) return 'wallet';
  return 'unknown';
}

export function LookupPage() {
  const wallet = useWallet();
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const [input, setInput] = useState(initial);
  const [query, setQuery] = useState(initial);
  const [state, setState] = useState<SearchState>('idle');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const kind = useMemo(() => detectKind(query), [query]);

  const search = useCallback(async (raw: string) => {
    const clean = raw.trim();
    setQuery(clean);
    setResult(null);
    setError(null);
    if (detectKind(clean) === 'unknown') {
      setState('invalid');
      return;
    }
    setState('loading');
    try {
      const found = await api<LookupResult>(`/api/public/lookup?q=${encodeURIComponent(clean)}`);
      setResult(found);
      setState('found');
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setState('not-found');
        return;
      }
      setError(cause instanceof Error ? cause.message : 'Lookup failed.');
      setState('error');
    }
  }, []);

  useEffect(() => {
    if (initial) void search(initial);
  }, [initial, search]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const clean = input.trim();
    setParams(clean ? { q: clean } : {});
    if (!clean) {
      setQuery('');
      setResult(null);
      setState('idle');
    } else if (clean === initial) {
      void search(clean);
    }
  };

  const memberResult = result?.kind === 'member' ? result.member : null;
  const explorer = result?.kind === 'member' ? result.explorer : null;
  const transactionResult = result?.kind === 'transaction' ? result.transaction : null;
  const uplineAscending = explorer ? [...explorer.upline].reverse() : [];
  const visibleUpline = uplineAscending.slice(-5);

  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [selectedSlotCycle, setSelectedSlotCycle] = useState<number>(1);

  useEffect(() => {
    if (!explorer) {
      setSelectedSlot(null);
      return;
    }
    const firstActive = explorer.slots.find((slot) => slot.status !== 'locked');
    setSelectedSlot(firstActive?.slotNumber ?? explorer.projectedSlots[0]?.slotNumber ?? 1);
  }, [explorer]);

  // On-chain cycles of the selected slot (indexed SeatFilled seats), oldest first.
  const slotCycles = useMemo(
    () => (explorer && selectedSlot !== null ? explorer.cycles.filter((cycle) => cycle.slotNumber === selectedSlot).sort((a, b) => a.cycleNumber - b.cycleNumber) : []),
    [explorer, selectedSlot],
  );

  // The on-chain slot view of the selected slot (owner 2026-10-11: no rules projection) - all cycles, all twelve
  // positions, built by the API from the indexed SeatFilled events of a slot the contract activated.
  const slotView = useMemo(
    () => (explorer && selectedSlot !== null ? explorer.projectedSlots.find((slot) => slot.slotNumber === selectedSlot) ?? null : null),
    [explorer, selectedSlot],
  );
  const viewCycles = useMemo(() => slotView?.cycles ?? [], [slotView]);

  const availableCycleNumbers = useMemo(() => {
    const numbers = new Set<number>([...slotCycles.map((cycle) => cycle.cycleNumber), ...viewCycles.map((cycle) => cycle.cycleNumber)]);
    return [...numbers].sort((a, b) => a - b);
  }, [slotCycles, viewCycles]);

  useEffect(() => {
    // Land on the cycle still filling (the latest), so a recycled slot opens on live seats.
    setSelectedSlotCycle(availableCycleNumbers[availableCycleNumbers.length - 1] ?? 1);
  }, [availableCycleNumbers]);

  const selectSlot = (slotNumber: number) => {
    setSelectedSlot(slotNumber);
  };

  const realCycle = slotCycles.find((cycle) => cycle.cycleNumber === selectedSlotCycle) ?? null;
  // Slots the contract activated: indexed activations plus the API's on-chain slot views (sub-slots live only there).
  const activatedSlots = explorer
    ? [...explorer.slots.filter((slot) => slot.status !== 'locked').map((slot) => slot.slotNumber), ...explorer.projectedSlots.filter((slot) => slot.source === 'onchain').map((slot) => slot.slotNumber)]
    : [];
  // A slot the chain has not activated has no view: an empty scaffold, never invented seats.
  const displayCycleNodes = useMemo(
    () => buildMatrixNodes(realCycle ?? { slotNumber: selectedSlot ?? 1, cycleNumber: 1, status: 'active', totalPositions: 12, completedPositions: 0, startedAt: '', completedAt: null, placements: [] }),
    [realCycle, selectedSlot],
  );
  const selectedSlotSummary = explorer && selectedSlot !== null ? explorer.slots.find((slot) => slot.slotNumber === selectedSlot) : null;
  const referral = explorer?.referralIncome ?? null;

  return (
    <>
      <PageHeading eyebrow="UNIFIED CHAIN EXPLORER" title="Lookup anything" description="Resolve B-Dynamic IDs, wallet addresses and transaction hashes, with the complete sponsor tree, slots, cycles and activity for any member." actions={<StatusPill tone="green"><ShieldCheck size={13} /> Public data only</StatusPill>} />
      <section className="lookup-hero panel">
        <form onSubmit={submit}>
          <Search />
          <input value={input} onChange={(event) => setInput(event.target.value)} autoFocus maxLength={80} autoComplete="off" spellCheck={false} aria-label="B-Dynamic lookup" placeholder="BD-1, 0x wallet, or transaction hash" />
          <button className="button primary" type="submit" disabled={state === 'loading'}>{state === 'loading' ? 'Searching...' : 'Explore'} <ArrowRight size={16} /></button>
        </form>
      </section>

      {state === 'idle' ? (
        <section className="lookup-categories">
          <article className="panel"><span><UserRoundSearch /></span><h3>Member record</h3><p>Sponsor lineage, downline tree, slots, cycles, tokens and activity for any B-Dynamic ID.</p></article>
          <article className="panel"><span><Wallet /></span><h3>Wallet association</h3><p>Resolve the indexed B-Dynamic member associated with a wallet.</p></article>
          <article className="panel"><span><Blocks /></span><h3>Transaction proof</h3><p>Inspect indexed finality, chain, block, and observation time.</p></article>
        </section>
      ) : null}

      <AnimatePresence mode="wait">
        {state === 'loading' ? <motion.section key="loading" className="panel lookup-result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><EmptyState title="Reading the indexed chain data" description="The lookup is in progress." /></motion.section> : null}
        {state === 'invalid' ? <motion.section key="invalid" className="panel lookup-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}><EmptyState title="No recognizable identifier" description="Use a B-Dynamic ID such as BD-1, a valid EVM wallet, or a 32-byte transaction hash." /></motion.section> : null}
        {state === 'not-found' ? <motion.section key="missing" className="panel lookup-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}><EmptyState title="No finalized record found" description={`The index contains no canonical record for ${query}.`} /></motion.section> : null}
        {state === 'error' ? <motion.section key="error" className="panel lookup-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}><EmptyState title="Lookup unavailable" description={error ?? 'The indexed chain data could not be reached.'} /></motion.section> : null}

        {state === 'found' && memberResult && explorer ? (
          <motion.div key="member" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <section className="lookup-results">
              <article className="panel lookup-profile">
                <div className="profile-orbit"><span>BD</span><i /></div>
                <div><span className="eyebrow">INDEXED MEMBER RECORD</span><h2>{memberResult.bdId}</h2><p>{shortenAddress(memberResult.walletAddress, 8)}</p></div>
                <StatusPill tone={memberResult.status === 'active' ? 'green' : 'amber'}><CheckCircle2 size={12} /> {memberResult.status}</StatusPill>
                <dl>
                  <div><dt>Entry era</dt><dd>{memberResult.birthEra}</dd></div>
                  <div><dt>Sponsor ID</dt><dd>{memberResult.sponsorBdId ?? 'Root'}</dd></div>
                  <div><dt>Leaders Club</dt><dd>{explorer.club.currentClubLabel ?? 'none'}</dd></div>
                  <div><dt>Joined</dt><dd>{formatDate(memberResult.joinedAt)}</dd></div>
                  <div><dt>Referral code</dt><dd>{memberResult.referralCode}</dd></div>
                  <div><dt>Wallet</dt><dd>{memberResult.walletAddress}</dd></div>
                </dl>
              </article>
              <div className="lookup-side">
                <article className="panel quick-stats-card">
                  <h3>At a glance (on-chain)</h3>
                  <div className="quick-stats-grid">
                    <div title="Sum of the vault's RewardPaid events (net, lifetime)"><span>Total earned</span><strong>{formatToken(atomicToDecimal(explorer.stats.totalEarnedAtomic))}</strong></div>
                    <div title="Vault balance not yet withdrawn (RewardPaid accrued minus Claimed)"><span>Claimable now</span><strong>{formatToken(atomicToDecimal(explorer.stats.claimableAtomic ?? explorer.stats.redeemableAtomic))}</strong></div>
                    {explorer.stats.withdrawnAtomic !== undefined ? <div title="Vault Claimed events"><span>Withdrawn</span><strong>{formatToken(atomicToDecimal(explorer.stats.withdrawnAtomic))}</strong></div> : null}
                    <div title="Placed in the matrix on-chain"><span>Direct partners</span><strong>{explorer.stats.directMembers.toLocaleString()}</strong></div>
                    <div title="Placed descendants on-chain"><span>Team members</span><strong>{explorer.stats.teamMembers.toLocaleString()}</strong></div>
                    <div title="Recycle seats in this ID's matrices"><span>Completed cycles</span><strong>{explorer.stats.completedCycles.toLocaleString()}</strong></div>
                    <div title="Real slots the contract activated"><span>Active slots</span><strong>{explorer.stats.activeSlots}/16</strong></div>
                  </div>
                </article>
                <article className="panel lookup-flow">
                  <h3>Sponsor route</h3>
                  {uplineAscending.length === 0 ? (
                    <p className="empty-copy">This ID has no sponsor; it is a root of the network.</p>
                  ) : (
                    <div className="route-flow">
                      {uplineAscending.length > visibleUpline.length ? <><span>…</span><ChevronRight /></> : null}
                      {visibleUpline.map((node) => (
                        <Fragment key={node.bdId}>
                          <span>{node.bdId}</span>
                          <ChevronRight />
                        </Fragment>
                      ))}
                      <strong>{memberResult.bdId}</strong>
                    </div>
                  )}
                  <div className="route-total"><Network size={15} /><span>Generations to root</span><strong>{uplineAscending.length}</strong></div>
                </article>
                {referral ? (
                  <article className="panel referral-income-card">
                    <h3>Referral income (on-chain)</h3>
                    <div className="referral-income-rows">
                      <div>
                        <span>Generation 1 (direct sponsor) <em>{referral.levels?.[0]?.payments ?? 0} payment{(referral.levels?.[0]?.payments ?? 0) === 1 ? '' : 's'} received</em></span>
                        <strong>{formatToken(atomicToDecimal(referral.directTotalAtomic))}</strong>
                      </div>
                      <div>
                        <span>Generations 2–{referral.levelMaxDepth} <em>{referral.levelTwoToSixPartners} payment{referral.levelTwoToSixPartners === 1 ? '' : 's'} received</em></span>
                        <strong>{formatToken(atomicToDecimal(referral.levelTotalAtomic))}</strong>
                      </div>
                      {referral.receivedNetAtomic !== undefined ? (
                        <div><span>Reached the wallet / claimable <em>after the vault fee (RewardPaid)</em></span><strong>{formatToken(atomicToDecimal(referral.receivedNetAtomic))}</strong></div>
                      ) : null}
                    </div>
                    <div className="route-total"><CheckCircle2 size={15} /><span>Received</span><strong>{formatToken(atomicToDecimal(referral.receivedTotalAtomic ?? referral.projectedTotalAtomic))}</strong></div>
                    <p className="empty-copy referral-income-note">
                      Sum of the SponsorIncome events the contract paid to {memberResult.bdId}.
                      {referral.missedOwnEntryAtomic !== undefined ? ` This ID's own entry sent ${formatToken(atomicToDecimal(referral.missedOwnEntryAtomic))} (${referral.missedOwnEntryLevels ?? 0} level${referral.missedOwnEntryLevels === 1 ? '' : 's'}) to the Referral Treasury.` : ''}
                      {referral.notReceivedAtomic !== undefined && referral.notReceivedCount ? ` Not received by this ID: ${formatToken(atomicToDecimal(referral.notReceivedAtomic))} (${referral.notReceivedCount} payment${referral.notReceivedCount === 1 ? '' : 's'} sent to the Referral Treasury).` : ''}
                    </p>
                  </article>
                ) : null}
              </div>
            </section>

            <section className="panel lookup-slots">
              <div className="panel-heading"><div><span className="eyebrow">SLOT PROGRESSION</span><h2>16-slot journey</h2></div><StatusPill tone="violet"><Grid2X2 size={12} /> {explorer.stats.activeSlots} active</StatusPill></div>
              <SlotTrack
                activeSlots={activatedSlots}
                virtualSlots={explorer.projectedSlots.filter((slot) => slot.virtual).map((slot) => slot.slotNumber)}
                selected={selectedSlot}
                onSelect={selectSlot}
                label={`${explorer.stats.activeSlots} of 16 slots active`}
              />
              <TrackLegend>
                {selectedSlot !== null && selectedSlot !== 1 ? <button type="button" className="button ghost small" onClick={() => selectSlot(1)}>Back to slot 1</button> : null}
              </TrackLegend>
              <p className="empty-copy">
                Click a slot to view its own cycle history below. A slot is not limited to a fixed number of cycles — it recycles indefinitely as positions fill.
                {selectedSlotSummary ? ` Slot ${selectedSlotSummary.slotNumber} · ${selectedSlotSummary.slotType} · ${selectedSlotSummary.status}${selectedSlotSummary.activatedAt ? ` · activated ${formatDate(selectedSlotSummary.activatedAt)}` : ''}.` : ''}
              </p>
            </section>

            <section className="panel audit-table"><div className="panel-heading"><div><span className="eyebrow">ALL SLOTS · SAME AS ADMIN ID BROWSER</span><h2>Slot state of {memberResult.bdId}</h2></div></div>
              <SlotStateTable onChainSlots={explorer.slots.filter((slot) => slot.status !== 'locked').map((slot) => slot.slotNumber)} projectedSlots={explorer.projectedSlots} activatedAt={Object.fromEntries(explorer.slots.map((slot) => [slot.slotNumber, slot.activatedAt]))} />
            </section>

            <ClubCard club={explorer.club} />

            <IdReportsPanel bdId={memberResult.bdId} />

            <PrimaryTokenCard token={explorer.primaryToken} />

            <CdtBenefitCard bdId={memberResult.bdId} token={explorer.primaryToken} view={explorer.cdtBenefit} readOnly />

            <OnChainMatrixSection bdId={explorer.primaryToken.bdId} />

            <GenerationIncomeMap bdId={memberResult.bdId} onSelectId={(id) => { setInput(id); setParams({ q: id }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />

            <GenerationExplorer
              rootBdId={memberResult.bdId}
              eyebrow="DOWNLINE NETWORK"
              title="Generation explorer"
            />

            <section className="panel matrix-panel">
              <div className="matrix-panel-heading">
                <div><span className="eyebrow">ON-CHAIN PLACEMENT MAP · SLOT {selectedSlot === null ? '' : slotLabel(selectedSlot)}{slotView?.virtual ? ' · VIRTUAL' : ''}</span><h2>Cycle {selectedSlotCycle} topology</h2></div>
                <div><StatusPill tone={realCycle?.status === 'completed' ? 'green' : realCycle ? 'violet' : 'amber'}>{realCycle ? realCycle.status : slotView ? 'No seats yet' : 'Not activated on-chain'}</StatusPill><button className="icon-button" type="button" title="Every seat is an indexed SeatFilled event"><Eye size={17} /></button></div>
              </div>
              {slotView ? (
                <div className="lookup-position-grid">
                  <ProjectedSlotPanel slot={slotView} onChain={selectedSlotSummary ? selectedSlotSummary.status !== 'locked' || slotView.source === 'onchain' : slotView.source === 'onchain'} selectedCycle={selectedSlotCycle} onSelectCycle={setSelectedSlotCycle} columns={6} />
                </div>
              ) : (
                <>
                  <CycleTabs cycles={availableCycleNumbers.length > 0 ? availableCycleNumbers : [1]} selected={selectedSlotCycle} onSelect={setSelectedSlotCycle} currentCycle={null} />
                  <p className="empty-copy">The contract has not activated slot {selectedSlot === null ? '' : slotLabel(selectedSlot)} for {memberResult.bdId}: no seat exists on-chain.</p>
                  <MatrixGraph nodes={displayCycleNodes} rootId={memberResult.bdId} cycle={selectedSlotCycle} />
                </>
              )}
            </section>

            <div className="detail-columns">
              <article className="panel routing-ledger">
                <div className="panel-heading"><div><span className="eyebrow">CYCLE HISTORY · SLOT {selectedSlot === null ? '' : slotLabel(selectedSlot)} · ON-CHAIN</span><h2>Cycles &amp; placements</h2></div><StatusPill tone="violet">{slotCycles.length} cycle{slotCycles.length === 1 ? '' : 's'}</StatusPill></div>
                {slotCycles.length === 0 ? <p className="empty-copy">No cycle exists on-chain for this slot.</p> : null}
                {[...slotCycles].reverse().map((cycle) => (
                  <div key={cycle.cycleNumber}>
                    <div className="cycle-head"><strong>Cycle {cycle.cycleNumber}</strong><span>{cycle.completedPositions}/{cycle.totalPositions} positions</span><StatusPill tone={cycle.status === 'completed' ? 'green' : cycle.status === 'paused' ? 'amber' : 'violet'}>{cycle.status}</StatusPill></div>
                    {cycle.placements.length > 0 ? (
                      <>
                        <div className="ledger-head"><span>Pos</span><span>Member</span><span>Seat</span><span>Amount</span><span>Tx</span></div>
                        {cycle.placements.map((placement) => (
                          <div className="ledger-row" key={`${cycle.cycleNumber}-${placement.positionNumber}`}>
                            <span>{placement.positionNumber}</span>
                            <span>{placement.memberBdId}</span>
                            <span style={{ textTransform: 'capitalize' }}>{placement.kind ? POSITION_KIND_LABEL[placement.kind] ?? placement.kind.replace(/_/g, ' ') : placement.placementType}</span>
                            <strong>{formatToken(atomicToDecimal(placement.amountAtomic))}</strong>
                            {placement.txHash ? <a href={`${explorerUrl(explorer.recentTransactions[0]?.chainId ?? wallet.expectedChainId)}/tx/${placement.txHash}`} target="_blank" rel="noreferrer"><ExternalLink size={13} /></a> : <span>—</span>}
                          </div>
                        ))}
                      </>
                    ) : <p className="empty-copy">No placements recorded for this cycle.</p>}
                  </div>
                ))}
              </article>
              <aside className="stack-column">
                <article className="panel lookup-journey">
                  <h3>Recent activity</h3>
                  {explorer.recentTransactions.length === 0 ? <p className="empty-copy">No indexed transactions yet.</p> : null}
                  {explorer.recentTransactions.map((transaction) => (
                    <div className="journey-event" key={transaction.txHash}>
                      <span><Activity /></span>
                      <div><strong style={{ textTransform: 'capitalize' }}>{transaction.kind.replace(/_/g, ' ')}</strong><small>{formatToken(atomicToDecimal(transaction.amountAtomic))} · {transaction.status}</small></div>
                      <em>{formatDate(transaction.observedAt)}</em>
                    </div>
                  ))}
                </article>
                <article className="panel lookup-tokens">
                  <h3>Utility tokens</h3>
                  {explorer.tokens.length === 0 ? <p className="empty-copy">No token minted on-chain for this ID yet.</p> : null}
                  {explorer.tokens.map((token) => (
                    <div className="token-event" key={token.tokenCode}>
                      <span><TicketCheck size={14} /></span>
                      <div><strong>{token.tokenCode}</strong><small>{token.status ?? token.state}{token.providerCode ? ` · ${token.providerCode}` : ''}</small></div>
                    </div>
                  ))}
                </article>
              </aside>
            </div>
          </motion.div>
        ) : null}

        {state === 'found' && transactionResult ? (
          <motion.section key="tx" className="lookup-results tx-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <article className="panel tx-proof">
              <div className="tx-proof-top"><span><Activity /></span><div><span className="eyebrow">INDEXED TRANSACTION</span><h2>{shortenAddress(transactionResult.txHash, 12)}</h2></div><StatusPill tone={transactionResult.status === 'confirmed' ? 'green' : 'amber'}><CheckCircle2 size={12} /> {transactionResult.status}</StatusPill></div>
              <dl>
                <div><dt>Chain ID</dt><dd>{transactionResult.chainId}</dd></div>
                <div><dt>Block</dt><dd>{transactionResult.blockNumber?.toLocaleString() ?? 'Pending'}</dd></div>
                <div><dt>Observed</dt><dd>{formatDate(transactionResult.observedAt)}</dd></div>
                <div><dt>Indexer status</dt><dd>{transactionResult.status}</dd></div>
              </dl>
              <a className="button secondary" href={`${explorerUrl(transactionResult.chainId)}/tx/${transactionResult.txHash}`} target="_blank" rel="noreferrer">Open in BscScan <ExternalLink size={15} /></a>
            </article>
          </motion.section>
        ) : null}
      </AnimatePresence>
      {kind === 'wallet' && state === 'found' ? <div className="integrity-note"><ShieldCheck size={17} /><span>Wallet lookup returns indexed protocol association only; no off-chain profile is used.</span></div> : null}
    </>
  );
}
